import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Send, Phone, Video, MoreVertical, 
  Check, CheckCheck, Clock, Smile, Paperclip, 
  Search, MessageCircle, User, Calendar, IndianRupee
} from 'lucide-react';
import { toast } from 'sonner';
import { api, useAuth } from '../App';
import { formatCurrency } from '../utils/currency';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

// Format time like WhatsApp
const formatMessageTime = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};

// Format date header
const formatDateHeader = (dateStr) => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  }
};

// Group messages by date
const groupMessagesByDate = (messages) => {
  const groups = {};
  messages.forEach(msg => {
    const date = new Date(msg.created_at).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(msg);
  });
  return groups;
};

const ChatPage = () => {
  const { conversationId, bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [pollingActive, setPollingActive] = useState(true);

  // Get or create conversation
  useEffect(() => {
    const initConversation = async () => {
      try {
        let convData;
        if (conversationId) {
          const res = await api.get(`/conversations/${conversationId}`);
          convData = res.data;
        } else if (bookingId) {
          const res = await api.get(`/conversations/booking/${bookingId}`);
          convData = res.data;
        }
        setConversation(convData);
        if (convData) {
          fetchMessages(convData.id);
        }
      } catch (err) {
        console.error('Error loading conversation:', err);
        toast.error('Failed to load conversation');
      } finally {
        setLoading(false);
      }
    };

    initConversation();
  }, [conversationId, bookingId]);

  // Poll for new messages
  useEffect(() => {
    if (!conversation?.id || !pollingActive) return;

    const pollInterval = setInterval(() => {
      fetchMessages(conversation.id, true);
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [conversation?.id, pollingActive]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async (convId, silent = false) => {
    try {
      const res = await api.get(`/messages/${convId}`);
      setMessages(res.data || []);
    } catch (err) {
      if (!silent) {
        console.error('Error fetching messages:', err);
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || sending || !conversation) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // Optimistic update
    const tempMessage = {
      id: `temp-${Date.now()}`,
      conversation_id: conversation.id,
      sender_id: user.id,
      sender_type: user.role,
      sender_name: user.name,
      content: messageText,
      message_type: 'text',
      read: false,
      created_at: new Date().toISOString(),
      pending: true
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      await api.post('/messages', {
        conversation_id: conversation.id,
        content: messageText,
        message_type: 'text'
      });
      // Refresh messages to get real message
      fetchMessages(conversation.id);
    } catch (err) {
      toast.error('Failed to send message');
      // Remove optimistic message
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
      setNewMessage(messageText);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getOtherPartyName = () => {
    if (!conversation) return 'Chat';
    return user.role === 'client' ? conversation.lawyer_name : conversation.client_name;
  };

  const getOtherPartyInitial = () => {
    const name = getOtherPartyName();
    return name?.charAt(0)?.toUpperCase() || '?';
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-slate-500">Loading chat...</p>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <MessageCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900">Conversation not found</h2>
          <p className="text-slate-500 mt-2">This conversation may have been deleted or you don't have access.</p>
          <Button onClick={() => navigate(-1)} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const messageGroups = groupMessagesByDate(messages);

  return (
    <div className="h-screen flex flex-col bg-[#efeae2]" data-testid="chat-page">
      {/* WhatsApp-style Header */}
      <header className="bg-[#075e54] text-white px-4 py-3 flex items-center gap-3 shadow-md">
        <button 
          onClick={() => navigate(-1)} 
          className="p-1 hover:bg-white/10 rounded-full transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        
        <div className="w-10 h-10 bg-slate-300 rounded-full flex items-center justify-center text-slate-700 font-semibold">
          {getOtherPartyInitial()}
        </div>
        
        <div className="flex-1 min-w-0">
          <h1 className="font-semibold truncate">{getOtherPartyName()}</h1>
          <p className="text-xs text-emerald-200 truncate">
            {conversation.booking?.consultation_mode === 'chat' && 'Chat Consultation'}
            {conversation.booking?.consultation_mode === 'call' && 'Call Consultation'}
            {conversation.booking?.consultation_mode === 'in-person' && 'In-Person Consultation'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <Phone className="w-5 h-5" />
          </button>
          <button className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <Video className="w-5 h-5" />
          </button>
          <button className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <MoreVertical className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Booking Info Banner */}
      {conversation.booking && (
        <div className="bg-[#dcf8c6] px-4 py-2 text-sm border-b border-emerald-200">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 text-slate-700">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {conversation.booking.date} at {conversation.booking.time_slot}
              </span>
              <span className="flex items-center gap-1">
                <IndianRupee className="w-4 h-4" />
                {formatCurrency(conversation.booking.consultation_fee)}
              </span>
            </div>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              conversation.booking.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
              conversation.booking.status === 'pending' ? 'bg-amber-100 text-amber-700' :
              conversation.booking.status === 'completed' ? 'bg-blue-100 text-blue-700' :
              'bg-slate-100 text-slate-700'
            }`}>
              {conversation.booking.status?.toUpperCase()}
            </span>
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div 
        className="flex-1 overflow-y-auto px-4 py-4"
        style={{ 
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c8d6cf' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      >
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center bg-white/80 rounded-lg px-6 py-4 shadow-sm">
              <MessageCircle className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
              <p className="text-slate-600">No messages yet</p>
              <p className="text-sm text-slate-400">Send a message to start the conversation</p>
            </div>
          </div>
        ) : (
          Object.entries(messageGroups).map(([date, msgs]) => (
            <div key={date}>
              {/* Date Header */}
              <div className="flex justify-center my-4">
                <span className="bg-white/90 text-slate-500 text-xs px-3 py-1 rounded-lg shadow-sm">
                  {formatDateHeader(msgs[0].created_at)}
                </span>
              </div>

              {/* Messages */}
              {msgs.map((msg, idx) => {
                const isOwn = msg.sender_id === user.id;
                return (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className={`flex mb-1 ${isOwn ? 'justify-end' : 'justify-start'}`}
                    data-testid={`message-${idx}`}
                  >
                    <div 
                      className={`max-w-[75%] rounded-lg px-3 py-2 shadow-sm ${
                        isOwn 
                          ? 'bg-[#dcf8c6] rounded-tr-none' 
                          : 'bg-white rounded-tl-none'
                      }`}
                    >
                      {/* Sender name for received messages */}
                      {!isOwn && (
                        <p className="text-xs text-emerald-600 font-semibold mb-1">
                          {msg.sender_name}
                        </p>
                      )}
                      
                      <p className="text-slate-800 whitespace-pre-wrap break-words">
                        {msg.content}
                      </p>
                      
                      <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        <span className="text-[10px] text-slate-400">
                          {formatMessageTime(msg.created_at)}
                        </span>
                        {isOwn && (
                          msg.pending ? (
                            <Clock className="w-3 h-3 text-slate-400" />
                          ) : msg.read ? (
                            <CheckCheck className="w-3 h-3 text-blue-500" />
                          ) : (
                            <CheckCheck className="w-3 h-3 text-slate-400" />
                          )
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input - WhatsApp Style */}
      <div className="bg-[#f0f0f0] px-4 py-3">
        <form onSubmit={handleSend} className="flex items-center gap-2">
          <button 
            type="button" 
            className="p-2 text-slate-500 hover:text-slate-700 transition-colors"
          >
            <Smile className="w-6 h-6" />
          </button>
          
          <button 
            type="button" 
            className="p-2 text-slate-500 hover:text-slate-700 transition-colors"
          >
            <Paperclip className="w-6 h-6" />
          </button>
          
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message"
            className="flex-1 bg-white rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 border border-slate-200"
            data-testid="message-input"
          />
          
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className={`p-3 rounded-full transition-colors ${
              newMessage.trim() && !sending
                ? 'bg-[#075e54] text-white hover:bg-[#054c44]'
                : 'bg-slate-300 text-slate-500'
            }`}
            data-testid="send-btn"
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatPage;
